"use client";

import React, { useState } from "react";
import { Check, Loader, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
export default function TheClarityToolkit() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll();
  return <section id="the-clarity-toolkit" ref={sectionRef} className={cn("dark bg-primary text-primary-foreground py-24 relative overflow-hidden", isVisible ? "opacity-100" : "opacity-0 transition-opacity duration-700")}>
      {}
      <div className="absolute inset-0 bg-[url('https://wpvc-images.s3.us-east-1.amazonaws.com/images/1573125/img/noise_texture.png')] opacity-5 pointer-events-none mix-blend-overlay"></div>

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {}
          <div className={cn("relative flex justify-center lg:justify-end transition-all duration-1000 delay-300", isVisible ? "translate-x-0 opacity-100" : "-translate-x-10 opacity-0")}>
            <div className="relative group perspective-1000">
              {}
              <div className="absolute -inset-4 bg-black/20 blur-2xl rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>
              
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1573125/img/printed_checklist_mockup.png" alt="30-Day Decluttering Challenge Checklist Mockup" className="relative w-full max-w-md lg:max-w-lg h-auto object-contain drop-shadow-2xl transform rotate-[-2deg] transition-transform duration-500 hover:rotate-0 hover:scale-105" />
              
              {}
              <div className="absolute -top-6 -right-6 bg-[hsl(35_25%_85%)] text-[hsl(210_10%_15%)] text-xs font-bold px-4 py-2 rounded-full shadow-lg rotate-12 animate-pulse">
                Free Download
              </div>
            </div>
          </div>

          {}
          <div className={cn("flex flex-col gap-8 max-w-xl transition-all duration-1000 delay-500", isVisible ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0")}>
            <div className="space-y-4">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif font-medium leading-tight tracking-tight">
                Download the 30-Day Decluttering Challenge.
              </h2>
              <p className="text-lg text-primary-foreground/80 leading-relaxed">
                Start your journey to a calmer home today. Join our newsletter and get this exclusive, printable guide delivered straight to your inbox.
              </p>
            </div>

            <div className="bg-background/10 backdrop-blur-sm p-6 md:p-8 rounded-xl border border-primary-foreground/10 shadow-xl">
              <FormProvider formId="clarity_toolkit_form_home" sectionName="the-clarity-toolkit" defaultValues={{
              full_name: "",
              email: "",
              interest_area: "",
              subscribe: false
            }} validationRules={{
              full_name: {
                required: true,
                minLength: 2,
                maxLength: 50
              },
              email: {
                required: true,
                pattern: "email"
              }
            }} submitText="Get the Guide" successMessage="Checklist sent! Please check your inbox.">
                <ToolkitForm />
              </FormProvider>
            </div>
            
            <div className="flex items-center gap-4 text-sm text-primary-foreground/60">
              <div className="flex items-center gap-1">
                <Check className="w-4 h-4" />
                <span>Printable PDF</span>
              </div>
              <div className="flex items-center gap-1">
                <Check className="w-4 h-4" />
                <span>Instant Access</span>
              </div>
              <div className="flex items-center gap-1">
                <Check className="w-4 h-4" />
                <span>Free Updates</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}
function ToolkitForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-8 text-center space-y-4 animate-in fade-in zoom-in duration-500">
        <div className="w-16 h-16 bg-[hsl(35_25%_85%)] rounded-full flex items-center justify-center shadow-lg">
          <Check className="w-8 h-8 text-[hsl(210_10%_15%)]" />
        </div>
        <h3 className="text-2xl font-serif font-medium">{successMessage}</h3>
        <p className="text-primary-foreground/80">Your download link is on its way.</p>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FormField control={control} name="full_name" rules={{
        required: "Name is required",
        minLength: {
          value: 2,
          message: "Min 2 characters"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-primary-foreground">Full Name</FormLabel>
              <FormControl>
                <Input placeholder="Jane Doe" {...field} className="bg-background/20 border-primary-foreground/20 text-primary-foreground placeholder:text-primary-foreground/50 focus-visible:ring-primary-foreground/50" />
              </FormControl>
              <FormMessage className="text-red-300" />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-primary-foreground">Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="jane@example.com" {...field} className="bg-background/20 border-primary-foreground/20 text-primary-foreground placeholder:text-primary-foreground/50 focus-visible:ring-primary-foreground/50" />
              </FormControl>
              <FormMessage className="text-red-300" />
            </FormItem>} />
      </div>

      <FormField control={control} name="interest_area" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-primary-foreground">Area of Interest (Optional)</FormLabel>
            <FormControl>
              <select {...field} className="flex h-9 w-full rounded-md border border-primary-foreground/20 bg-background/20 px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-primary-foreground/50 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary-foreground/50 disabled:cursor-not-allowed disabled:opacity-50 text-primary-foreground">
                <option value="" className="bg-primary text-primary-foreground">Select an area</option>
                <option value="Home Organization" className="bg-primary text-primary-foreground">Home Organization</option>
                <option value="Decluttering" className="bg-primary text-primary-foreground">Decluttering</option>
                <option value="Storage Solutions" className="bg-primary text-primary-foreground">Storage Solutions</option>
                <option value="Cleaning Routines" className="bg-primary text-primary-foreground">Cleaning Routines</option>
                <option value="Minimalist Living" className="bg-primary text-primary-foreground">Minimalist Living</option>
              </select>
            </FormControl>
            <FormMessage className="text-red-300" />
          </FormItem>} />

      <FormField control={control} name="subscribe" render={({
      field
    }) => {}} />

      {submitError && <div className="p-3 bg-red-500/20 border border-red-500/30 rounded-md">
          <p className="text-red-200 text-sm">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full bg-[hsl(35_25%_85%)] text-[hsl(210_10%_15%)] hover:bg-[hsl(35_25%_75%)] font-medium py-6 text-base shadow-lg hover:shadow-xl transition-all">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 h-5 w-5" />
            Processing...
          </> : <>
            <Download className="mr-2 h-5 w-5" />
            Get the Guide
          </>}
      </Button>
    </Form>;
}